import { useState } from "react";
import { Users, Crown, Medal, TrendingUp, CheckCircle, Swords } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Community {
  id: string;
  name: string;
  image?: string | null;
  member_count: number | null;
  total_volume_sol: number | null;
  total_profit_sol: number | null;
  is_verified?: boolean | null;
  tag?: string | null;
}

interface CommunityLeaderboardProps {
  communities: Community[];
  isLoading: boolean;
}

export function CommunityLeaderboard({ communities, isLoading }: CommunityLeaderboardProps) {
  const [podiumImageLoaded, setPodiumImageLoaded] = useState<Record<string, boolean>>({});
  const [tableImageLoaded, setTableImageLoaded] = useState<Record<string, boolean>>({});

  const formatNumber = (num: number | null) => {
    if (!num) return "0";
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-sm font-mono text-muted-foreground">#{rank}</span>;
  };

  const getRankStyles = (rank: number) => {
    if (rank === 1) return "bg-gradient-to-r from-yellow-500/20 to-transparent border-yellow-500/40";
    if (rank === 2) return "bg-gradient-to-r from-gray-400/15 to-transparent border-gray-400/30";
    if (rank === 3) return "bg-gradient-to-r from-amber-600/15 to-transparent border-amber-600/30";
    return "bg-card border-border/50 hover:border-border";
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="p-4 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-4">
              <Skeleton className="w-12 h-12 rounded-xl" />
              <div className="flex-1">
                <Skeleton className="h-5 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!communities?.length) {
    return (
      <div className="p-12 text-center rounded-xl bg-card border border-border">
        <Users className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
        <p className="text-lg font-medium text-foreground mb-2">No communities yet</p>
        <p className="text-muted-foreground mb-4">Create a trading squad and compete!</p>
        <Button variant="hero">
          <Swords className="w-4 h-4 mr-2" />
          Create Squad
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Top 3 Podium */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[1, 0, 2].map((podiumIndex) => {
          const community = communities[podiumIndex];
          if (!community) return <div key={podiumIndex} />;
          const rank = podiumIndex === 1 ? 2 : podiumIndex === 0 ? 1 : 3;

          return (
            <div
              key={community.id}
              tabIndex={0}
              role="button"
              aria-label={`View community ${community.name} details`}
              className={cn(
                "p-5 rounded-2xl border-2 text-center transition-all hover-lift cursor-pointer focus-visible:ring-2 focus-visible:ring-primary",
                rank === 1 && "sm:order-2 sm:-mt-4",
                rank === 2 && "sm:order-1",
                rank === 3 && "sm:order-3",
                getRankStyles(rank)
              )}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  // In a real app, navigate to community profile
                  console.log(`Navigating to community: ${community.id}`);
                }
              }}
              onClick={() => console.log(`Navigating to community: ${community.id}`)}
            >
              <div className="w-16 h-16 mx-auto mb-3 rounded-xl bg-secondary overflow-hidden flex items-center justify-center relative">
                {community.image ? (
                  <>
                    {!podiumImageLoaded[community.id] && <Skeleton className="absolute inset-0" />}
                    <img
                      src={community.image}
                      alt={community.name}
                      className={cn("w-full h-full object-cover", !podiumImageLoaded[community.id] && "opacity-0")}
                      onLoad={() => setPodiumImageLoaded(prev => ({ ...prev, [community.id]: true }))}
                      onError={() => setPodiumImageLoaded(prev => ({ ...prev, [community.id]: true }))}
                    />
                  </>
                ) : (
                  <span className="text-2xl font-bold text-primary">{community.name.charAt(0)}</span>
                )}
              </div>
              <div className="flex items-center justify-center gap-2 mb-1">
                <p className="font-semibold text-foreground truncate">{community.name}</p>
                {community.is_verified && <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />}
              </div>
              {community.tag && (
                <Badge variant="secondary" className="text-xs mb-2">{community.tag}</Badge>
              )}
              <p className={cn(
                "text-xl font-mono font-bold",
                (community.total_profit_sol ?? 0) >= 0 ? "text-success" : "text-destructive"
              )}>
                {(community.total_profit_sol ?? 0) >= 0 ? "+" : ""}{formatNumber(community.total_profit_sol)} SOL
              </p>
              <div className="flex items-center justify-center gap-3 mt-2 text-xs text-muted-foreground">
                <span><Users className="w-3 h-3 inline mr-1" />{formatNumber(community.member_count)}</span>
                <span><TrendingUp className="w-3 h-3 inline mr-1" />{formatNumber(community.total_volume_sol)} Vol</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Full List */}
      <ScrollArea className="w-full rounded-xl border border-border">
        <div className="min-w-[600px]">
          <table className="w-full" role="table">
            <thead>
              <tr>
                <th scope="col" className="text-left p-3 text-xs sm:text-sm">Rank</th>
                <th scope="col" className="text-left p-3 text-xs sm:text-sm">Community</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Members</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Volume</th>
                <th scope="col" className="text-right p-3 text-xs sm:text-sm">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {communities.slice(3).map((community, index) => {
                const rank = index + 4;

                return (
                  <tr key={community.id} tabIndex={0} role="button" aria-label={`View community ${community.name} details`} className={cn("hover:bg-secondary/50 transition-colors focus-visible:ring-2 focus-visible:ring-primary", getRankStyles(rank))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        console.log(`Navigating to community: ${community.id}`);
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        const next = e.currentTarget.nextElementSibling as HTMLElement;
                        if (next) next.focus();
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        const prev = e.currentTarget.previousElementSibling as HTMLElement;
                        if (prev) prev.focus();
                      }
                    }}
                    onClick={() => console.log(`Navigating to community: ${community.id}`)}
                  >
                    <td className="p-3 text-xs sm:text-sm">
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                        {getRankIcon(rank)}
                      </div>
                    </td>
                    <td className="p-3 text-xs sm:text-sm">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-secondary overflow-hidden flex-shrink-0 relative">
                          {community.image ? (
                            <>
                              {!tableImageLoaded[community.id] && <Skeleton className="absolute inset-0" />}
                              <img
                                src={community.image}
                                alt={community.name}
                                className={cn("w-full h-full object-cover", !tableImageLoaded[community.id] && "opacity-0")}
                                onLoad={() => setTableImageLoaded(prev => ({ ...prev, [community.id]: true }))}
                                onError={() => setTableImageLoaded(prev => ({ ...prev, [community.id]: true }))}
                              />
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg font-bold text-primary">
                              {community.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-foreground truncate">{community.name}</p>
                            {community.is_verified && <CheckCircle className="w-4 h-4 text-primary" />}
                            {community.tag && (
                              <Badge variant="secondary" className="text-xs">{community.tag}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className="text-sm font-mono text-foreground">
                        {formatNumber(community.member_count)}
                      </p>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className="text-sm font-mono text-foreground">
                        {formatNumber(community.total_volume_sol)} SOL
                      </p>
                    </td>
                    <td className="p-3 text-right text-xs sm:text-sm">
                      <p className={cn(
                        "font-mono font-bold",
                        (community.total_profit_sol ?? 0) >= 0 ? "text-success" : "text-destructive"
                      )}>
                        {(community.total_profit_sol ?? 0) >= 0 ? "+" : ""}{formatNumber(community.total_profit_sol)} SOL
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ScrollArea>
    </div>
  );
}
